// POLICY COMPARATOR — the AVRASTRO differentiator rebuilt (legacy v230/v232/v233): up to three plans side
// by side with the vocabulary kept strictly apart — Sum insured ≠ Monthly premium ≠ Total premium cost
// ≠ Savings contribution ≠ Projected value ≠ Guaranteed value. Results recompute as you type; the chart
// shows projected value against premiums paid; a decision freezes a signed snapshot (never edited again,
// only copied into a new draft).
import { useMemo, useState } from 'react';
import { comparePlans, emptyPlan, normalizePlans, saveComparison, decideComparison, copyComparison, COMPARISON_DECISIONS, type PlanInput, type ComparisonDecision, type PlanResult } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Avatar, Empty, Field, Modal, PersonPicker } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath, useRoute } from '../core/router.ts';
import { LineChart } from '../core/viz.tsx';

const PLAN_COLOR: Record<PlanInput['label'], string> = { A: 'var(--s1)', B: 'var(--s2)', C: 'var(--s3)' };

export function ComparatorView({ id }: { id?: string }) {
  if (!id) return <ComparatorList />;
  return <ComparatorEditor key={id} id={id === 'new' ? undefined : id} />;
}

function ComparatorList() {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const rows = [...view.rows('comparisons')].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const legacy = view.rows('legacy_records').filter((r) => r.kind === 'comparison');
  return (
    <div data-testid="comparator-list">
      <div className="page-head">
        <div><h1>{t('comparator.title')}</h1><p>{t('comparator.subtitle')}</p></div>
        <div className="page-actions"><button className="btn btn-primary" onClick={() => navigate('/comparator/new')} data-testid="comparator-new"><Icon name="plus" />{t('comparator.new')}</button></div>
      </div>
      <section className="card">
        <div className="card-head"><h3>{t('comparator.history')}</h3><span className="chip">{rows.length}</span></div>
        {rows.length ? <div className="agenda-list">{rows.map((c) => {
          const p = view.get('persons', c.personId);
          return (
            <div key={c.id} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }} onClick={() => navigate(`/comparator/${c.id}`)} role="button" tabIndex={0} data-testid="comparison-row">
              <Avatar name={p?.displayName ?? '?'} size="sm" />
              <div style={{ minWidth: 0 }}><div className="t one-line">{c.title}</div><div className="s">{[p?.displayName, i18n.dateTime(c.updatedAt), `${normalizePlans(c.plans).length} ${t('comparator.plans')}`].filter(Boolean).join(' · ')}</div></div>
              {c.status === 'decided' ? <span className="chip ok"><Icon name="lock" size={12} />{t(`comparator.decisions.${c.decision ?? 'none'}`)}</span> : <span className="chip gold">{t('comparator.draft')}</span>}
            </div>
          );
        })}</div> : <Empty icon="compare">{t('comparator.empty')}</Empty>}
      </section>
      {legacy.length > 0 && (
        <section className="card" style={{ marginTop: 18 }}>
          <div className="card-head"><div><h3>{t('comparator.legacy')}</h3><div className="small muted">{t('records.subtitle')}</div></div><span className="chip">{legacy.length}</span></div>
          <div className="agenda-list">{legacy.map((r) => {
            const p = r.personId ? view.get('persons', r.personId) : undefined;
            return <div key={r.id} className="agenda-item" onClick={() => p && navigate(personPath(p.id, 'records'))} role="button" tabIndex={0}><span className="kind-icon"><Icon name="records" /></span><div style={{ minWidth: 0 }}><div className="t one-line">{r.title || t('records.kind.comparison')}</div><div className="s">{[p?.displayName, r.date ? i18n.date(r.date) : ''].filter(Boolean).join(' · ')}</div></div><Icon name="arrow" size={16} /></div>;
          })}</div>
        </section>
      )}
    </div>
  );
}

function ComparatorEditor({ id }: { id?: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const route = useRoute();
  const row = id ? view.get('comparisons', id) : undefined;
  const locked = row?.status === 'decided';
  const [personId, setPersonId] = useState<string | null>(row?.personId ?? route.query.get('person'));
  const [title, setTitle] = useState(row?.title ?? '');
  const [plans, setPlans] = useState<PlanInput[]>(() => {
    if (row) return normalizePlans(locked && row.snapshot ? (row.snapshot as { plans?: unknown }).plans : row.plans);
    return [{ ...emptyPlan('A') }, { ...emptyPlan('B') }];
  });
  const [dirty, setDirty] = useState(false);
  const verdict = useMemo(() => comparePlans(plans), [plans]);
  const person = personId ? view.get('persons', personId) : undefined;
  if (id && !row) return <Empty icon="compare">{t('errors.comparison.not_found')}</Empty>;

  const set = (i: number, patch: Partial<PlanInput>) => { setPlans((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p))); setDirty(true); };
  const add = () => { setPlans((ps) => (ps.length >= 3 ? ps : normalizePlans([...ps, emptyPlan((['A', 'B', 'C'] as const)[ps.length]!)]))); setDirty(true); };
  const remove = (i: number) => { setPlans((ps) => normalizePlans(ps.filter((_, j) => j !== i))); setDirty(true); };
  const save = async () => {
    if (!personId) { rt.toast(t('comparator.need_person'), 'error'); return null; }
    const cs = await rt.run((v, c) => saveComparison(v, { id: row?.id, personId, title, plans }, c), 'comparator.saved');
    if (cs) { setDirty(false); if (!row) navigate(`/comparator/${cs.entityId}`, { replace: true }); }
    return cs;
  };
  const decide = async () => {
    const cs = dirty || !row ? await save() : { entityId: row.id };
    if (!cs?.entityId) return;
    rt.open(<DecideModal comparisonId={cs.entityId} plans={plans} />);
  };
  const copy = async () => { if (!row) return; const cs = await rt.run((v, c) => copyComparison(v, row.id, c), 'comparator.copied'); if (cs) navigate(`/comparator/${cs.entityId}`); };

  const money = (n: number) => i18n.money(n);
  const best = verdict.best;
  const cell = (label: PlanInput['label'], key: keyof typeof best) => (best[key] === label && plans.length > 1 ? 'best' : '');
  const years = Math.max(0, ...verdict.results.map((r) => r.years.length));
  const snap = locked ? (row?.snapshot as { decidedAt?: string; decision?: ComparisonDecision } | undefined) : undefined;

  return (
    <div data-testid="comparator">
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/comparator')} style={{ marginBottom: 10 }}><Icon name="back" />{t('comparator.history')}</button>
      <div className="page-head">
        <div style={{ minWidth: 0 }}><h1>{row ? row.title : t('comparator.new')}</h1><p>{person ? <span className="row" style={{ gap: 8, display: 'inline-flex' }}><Avatar name={person.displayName} size="xs" /><a onClick={() => navigate(personPath(person.id, 'comparisons'))} style={{ cursor: 'pointer' }}>{person.displayName}</a></span> : t('comparator.subtitle')}</p></div>
        {!locked && <div className="page-actions">
          {dirty && <span className="chip gold">{t('common.unsaved')}</span>}
          <button className="btn" onClick={() => void save()} data-testid="comparator-save"><Icon name="check" />{t('common.save')}</button>
          <button className="btn btn-primary" onClick={() => void decide()} data-testid="comparator-decide"><Icon name="lock" />{t('comparator.decide')}</button>
        </div>}
      </div>

      {locked && (
        <div className="locked-banner" data-testid="comparator-locked">
          <span className="row" style={{ gap: 10 }}><Icon name="lock" /><span><b>{t('comparator.decided', { date: i18n.date(row!.decidedAt ?? snap?.decidedAt ?? row!.updatedAt), decision: t(`comparator.decisions.${row!.decision ?? 'none'}`) })}</b><br /><span className="small muted">{t('comparator.locked')}</span></span></span>
          <button className="btn" onClick={() => void copy()} data-testid="comparator-copy"><Icon name="copy" />{t('comparator.copy')}</button>
        </div>
      )}

      {!locked && (
        <section className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="form-grid">
            <Field label={t('comparator.for')} group><PersonPicker value={personId} onChange={(v) => { setPersonId(v); setDirty(true); }} /></Field>
            <Field label={t('comparator.name')}><input className="input" value={title} placeholder={plans.map((p) => p.product || `${t('comparator.plan', { label: p.label })}`).join(' vs ')} onChange={(e: { target: HTMLInputElement }) => { setTitle(e.target.value); setDirty(true); }} data-testid="comparator-title" /></Field>
          </div>
        </section>
      )}

      <div className="cmp-grid" style={{ ['--plans' as string]: plans.length + (!locked && plans.length < 3 ? 1 : 0), marginBottom: 18 }}>
        {plans.map((p, i) => <PlanColumn key={p.label} plan={p} result={verdict.results[i]!} locked={locked} onChange={(patch) => set(i, patch)} onRemove={plans.length > 1 ? () => remove(i) : undefined} />)}
        {!locked && plans.length < 3 && <button className="card" onClick={add} style={{ display: 'grid', placeItems: 'center', gap: 8, minHeight: 200, cursor: 'pointer', color: 'var(--muted)', borderStyle: 'dashed' }} data-testid="comparator-add"><Icon name="plus" size={28} /><b>{t('comparator.add_plan')}</b></button>}
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 1fr)', alignItems: 'start' }}>
        <section className="card" data-testid="comparator-chart">
          <div className="card-head"><div><h3>{t('comparator.chart')}</h3><div className="small muted">{t('comparator.chart_hint')}</div></div></div>
          <div className="legend" style={{ padding: '4px 20px 0' }}>
            {plans.map((p) => <span key={p.label}><i className="line" style={{ background: PLAN_COLOR[p.label] }} />{t('comparator.plan', { label: p.label })}{p.product ? ` · ${p.product}` : ''}</span>)}
            <span><i className="line" style={{ background: 'var(--text-2)' }} />{t('comparator.projected')}</span>
            <span style={{ color: 'var(--text-2)' }}><i className="dash" />{t('comparator.chart_paid')}</span>
          </div>
          <div style={{ padding: '8px 12px 14px' }}>
            {years > 0 && verdict.results.some((r) => r.totalPaid > 0) ? <LineChart ariaLabel={t('comparator.chart')} format={(n) => i18n.money(n, undefined, { compact: true })} xLabel={(i) => t('comparator.year', { year: i + 1 })}
              series={[
                ...verdict.results.map((r) => ({ id: `p${r.label}`, label: `${t('comparator.chart_paid')} · ${r.label}`, color: PLAN_COLOR[r.label], values: r.years.map((y) => y.paidToDate), dashed: true })),
                ...verdict.results.filter((r) => r.totalSavingsContribution > 0).map((r) => ({ id: `v${r.label}`, label: `${t('comparator.projected')} · ${r.label}`, color: PLAN_COLOR[r.label], values: r.years.map((y) => y.fundValue), endLabel: r.label })),
              ]} /> : <Empty icon="insights">{t(years > 0 ? 'comparator.no_data' : 'comparator.no_term')}</Empty>}
          </div>
        </section>

        <section className="card" data-testid="comparator-results">
          <div className="card-head"><h3>{t('comparator.results')}</h3><span className="tiny muted">★ {t('comparator.best')}</span></div>
          <div style={{ overflowX: 'auto', padding: '0 4px 12px' }}>
            <table className="cmp-results">
              <thead><tr><th />{plans.map((p) => <th key={p.label}><span className="plan-badge" style={{ ['--plan' as string]: PLAN_COLOR[p.label], width: 24, height: 24, borderRadius: 8, fontSize: 12 }}>{p.label}</span></th>)}</tr></thead>
              <tbody>
                <tr className="section"><td colSpan={plans.length + 1}>{t('comparator.section_cost')}</td></tr>
                <Row label={t('comparator.monthly')} vals={plans.map((p) => ({ v: money(p.monthlyPremium), c: cell(p.label, 'lowestPremium') }))} />
                <Row label={t('comparator.total_paid')} vals={verdict.results.map((r) => ({ v: money(r.totalPaid) }))} />
                <Row label={t('comparator.net_cost')} vals={verdict.results.map((r) => ({ v: money(r.netCost), c: cell(r.label, 'lowestNetCost') }))} />
                <tr className="section"><td colSpan={plans.length + 1}>{t('comparator.section_protection')}</td></tr>
                <Row label={t('comparator.cover')} vals={plans.map((p) => ({ v: p.sumInsured ? money(p.sumInsured) : '—', c: cell(p.label, 'highestCover') }))} />
                <Row label={t('comparator.per1000')} vals={verdict.results.map((r) => ({ v: r.premiumPer1000Cover !== null ? money(r.premiumPer1000Cover) : '—', c: cell(r.label, 'bestCoverEfficiency') }))} />
                <Row label={t('comparator.protection_cost')} vals={verdict.results.map((r) => ({ v: money(r.costOfProtectionAndCharges) }))} />
                <tr className="section"><td colSpan={plans.length + 1}>{t('comparator.section_savings')}</td></tr>
                <Row label={t('comparator.projected')} vals={verdict.results.map((r) => ({ v: r.totalSavingsContribution ? money(r.projectedValue) : '—', c: cell(r.label, 'highestProjected') }))} testId="row-projected" />
                <Row label={t('comparator.guaranteed_value')} vals={verdict.results.map((r) => ({ v: r.guaranteedValue ? money(r.guaranteedValue) : '—' }))} />
                <Row label={t('comparator.charges_impact')} vals={verdict.results.map((r) => ({ v: r.totalSavingsContribution ? money(r.chargesImpact) : '—', c: cell(r.label, 'lowestCharges') }))} />
                <Row label={t('comparator.breakeven')} vals={verdict.results.map((r) => ({ v: !r.totalSavingsContribution ? '—' : r.breakEvenYear ? t('comparator.breakeven_year', { year: r.breakEvenYear }) : t('comparator.never') }))} />
              </tbody>
            </table>
          </div>
          <p className="tiny muted" style={{ padding: '0 20px 16px', margin: 0 }}>{t('comparator.disclaimer')}</p>
        </section>
      </div>
    </div>
  );
}

function Row({ label, vals, testId }: { label: string; vals: { v: string; c?: string }[]; testId?: string }) {
  return <tr data-testid={testId}><td>{label}</td>{vals.map((x, i) => <td key={i} className={x.c}>{x.v}</td>)}</tr>;
}

function PlanColumn({ plan, result, locked, onChange, onRemove }: { plan: PlanInput; result: PlanResult; locked: boolean; onChange: (p: Partial<PlanInput>) => void; onRemove?: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const num = (k: keyof PlanInput, label: string, step = 1) => (
    <Field label={label}><input className="input num" type="number" inputMode="decimal" step={step} disabled={locked} value={String(plan[k] ?? '')} data-testid={`plan-${plan.label}-${String(k)}`}
      onChange={(e: { target: HTMLInputElement }) => onChange({ [k]: e.target.value === '' ? 0 : Number(e.target.value) } as Partial<PlanInput>)} /></Field>
  );
  const txt = (k: 'insurer' | 'product' | 'quotationNumber', label: string) => (
    <Field label={label}><input className="input" disabled={locked} value={plan[k] ?? ''} data-testid={`plan-${plan.label}-${k}`} onChange={(e: { target: HTMLInputElement }) => onChange({ [k]: e.target.value })} /></Field>
  );
  const savings = plan.kind !== 'protection';
  return (
    <section className="card plan-col" style={{ ['--plan' as string]: PLAN_COLOR[plan.label] }} data-testid={`plan-${plan.label}`}>
      <div className="plan-head">
        <span className="row" style={{ gap: 10 }}><span className="plan-badge">{plan.label}</span><b>{t('comparator.plan', { label: plan.label })}</b></span>
        {onRemove && !locked && <button className="btn btn-ghost btn-sm" onClick={onRemove} aria-label={t('comparator.remove_plan')}><Icon name="close" size={14} /></button>}
      </div>
      <div className="form-grid">
        {txt('insurer', t('comparator.insurer'))}
        {txt('product', t('comparator.product'))}
        <Field label={t('comparator.kind')} span2>
          <select className="select" disabled={locked} value={plan.kind} onChange={(e: { target: HTMLSelectElement }) => { const kind = e.target.value as PlanInput['kind']; onChange({ kind, savingsSharePct: kind === 'protection' ? 0 : kind === 'savings' ? 100 : plan.savingsSharePct || 60 }); }}>
            {(['protection', 'savings', 'combined'] as const).map((k) => <option key={k} value={k}>{t(`comparator.kinds.${k}`)}</option>)}
          </select>
        </Field>
        {num('monthlyPremium', t('comparator.monthly'), 0.01)}
        {num('termYears', t('comparator.term'))}
        {num('sumInsured', t('comparator.cover'), 100)}
        {num('indexationPct', t('comparator.indexation'), 0.5)}
        {savings && <>
          {num('savingsSharePct', t('comparator.savings_share'))}
          {num('allocationDelayMonths', t('comparator.delay'))}
          {num('year1ChargePct', t('comparator.y1'))}
          {num('year2ChargePct', t('comparator.y2'))}
          {num('annualFeePct', t('comparator.fee'), 0.1)}
          {num('assumedReturnPct', t('comparator.return'), 0.1)}
          {num('guaranteedMaturity', t('comparator.guaranteed'), 100)}
        </>}
        {txt('quotationNumber', t('comparator.quotation'))}
      </div>
      {result.warnings.length > 0 && <div className="warns" data-testid={`plan-${plan.label}-warnings`}>{result.warnings.map((w) => <span key={w}><Icon name="spark" size={12} />{t(`comparator.warn.${w}`)}</span>)}</div>}
    </section>
  );
}

function DecideModal({ comparisonId, plans }: { comparisonId: string; plans: PlanInput[] }) {
  const rt = useRuntime();
  const { t } = rt;
  const [decision, setDecision] = useState<ComparisonDecision>('A');
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollow] = useState('');
  const options = COMPARISON_DECISIONS.filter((d) => d === 'later' || d === 'none' || plans.some((p) => p.label === d));
  const commit = async () => {
    const cs = await rt.run((v, c) => decideComparison(v, comparisonId, { decision, notes, followUpDate: followUpDate || null }, c), 'comparator.decided_toast');
    if (cs) rt.close();
  };
  return (
    <Modal title={t('comparator.decide')} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={() => void commit()} data-testid="decide-commit"><Icon name="lock" />{t('comparator.decide')}</button></>}>
      <div className="stack">
        <Field label={t('comparator.decision')} group>
          <div className="segmented">{options.map((d) => <button key={d} type="button" aria-pressed={decision === d} onClick={() => setDecision(d)}>{t(`comparator.decisions.${d}`)}</button>)}</div>
        </Field>
        <Field label={t('common.notes')}><textarea className="textarea" value={notes} onChange={(e: { target: HTMLTextAreaElement }) => setNotes(e.target.value)} /></Field>
        <Field label={t('comparator.followup')} hint={decision === 'later' ? t('comparator.followup_hint') : undefined} group><DateField value={followUpDate} onChange={(d) => setFollow(d)} /></Field>
        <p className="small muted" style={{ margin: 0 }}>{t('comparator.lock_note')}</p>
      </div>
    </Modal>
  );
}

